import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  UserIcon, 
  KeyIcon, 
  ComputerDesktopIcon, 
  CpuChipIcon,
  CalendarIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  TrashIcon,
  ChevronRightIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { Modal, Tabs, Badge, Card, CardContent, CardHeader, CardTitle, Button } from './ui';
import { clientService } from '../services/clientService';
import { licenseService } from '../services/licenseService';
import { formatDate } from '../utils/formatters';
import { CLIENT_TYPES, CLIENT_STATUS, LICENSE_STATUS } from '../constants';

const ClientDetailModal = ({ isOpen, onClose, clientId }) => {
  const [activeTab, setActiveTab] = useState('overview');
  /** Una sola instalación expandida a la vez para no apilar 20 PCs con historial completo */
  const [expandedInstallationId, setExpandedInstallationId] = useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    setExpandedInstallationId(null);
  }, [clientId, isOpen]);

  // Fetch client details (includes licenses and installations)
  const { data: client, isLoading: clientLoading } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientService.getClient(clientId),
    enabled: !!clientId && isOpen,
  });

  const releaseInstallationMutation = useMutation({
    mutationFn: ({ licenseId, installationId }) =>
      licenseService.releaseInstallation(licenseId, installationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
    },
  });

  const handleReleaseInstallation = (installation) => {
    const licenseId = installation.license_id;
    if (!licenseId || !installation.id) {
      window.alert('No se pudo identificar la licencia o la instalación.');
      return;
    }
    const ok = window.confirm(
      `¿Liberar este equipo y recuperar un cupo de instalación?\n\n` +
        `${installation.machine_name}\n${installation.mac_address}\n\n` +
        `La licencia ${installation.license_key} podrá activarse en un equipo nuevo.`
    );
    if (!ok) return;
    releaseInstallationMutation.mutate({
      licenseId,
      installationId: installation.id,
    });
  };

  // Extract data from client response
  const licenses = client?.licenses || [];
  const installations =
    client?.licenses?.flatMap((license) =>
      (license.installations || []).map((inst) => ({
        ...inst,
        license_id: inst.license_id ?? license.id,
      }))
    ) || [];

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'suspended': return 'danger';
      case 'pending': return 'warning';
      default: return 'secondary';
    }
  };

  const getTypeLabel = (type) => {
    const typeObj = CLIENT_TYPES.find(t => t.value === type);
    return typeObj ? typeObj.label : type;
  };

  const getStatusLabel = (status) => {
    const statusObj = CLIENT_STATUS.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const getLicenseTypeLabel = (license) => {
    // Usar directamente el campo license_type que ahora contiene el nombre del tipo definido por el usuario
    return license.license_type || 'Sin tipo';
  };

  const getLicenseStatusLabel = (status) => {
    const statusObj = LICENSE_STATUS.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const getLicenseStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'danger';
      case 'suspended': return 'warning';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  const getLicenseTypeBadgeVariant = (license) => {
    // Usar un color basado en el ID del LicenseType para consistencia
    const typeId = license.license_type_config || 0;
    const colors = ['info', 'success', 'warning', 'danger', 'secondary', 'primary'];
    return colors[typeId % colors.length];
  };

  const getValidationResultLabel = (result) => {
    const labels = {
      success: 'Exitosa',
      failed: 'Fallida',
      expired: 'Expirada',
      suspended: 'Suspendida',
      limit_exceeded: 'Límite excedido',
    };
    return labels[result] || result || '—';
  };

  const getValidationResultBadgeVariant = (result) => {
    switch (result) {
      case 'success':
        return 'success';
      case 'failed':
        return 'danger';
      case 'expired':
      case 'suspended':
        return 'warning';
      case 'limit_exceeded':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Hace un momento';
    if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} h`;
    if (diffInSeconds < 2592000) return `Hace ${Math.floor(diffInSeconds / 86400)} días`;
    if (diffInSeconds < 31536000) return `Hace ${Math.floor(diffInSeconds / 2592000)} meses`;
    return `Hace ${Math.floor(diffInSeconds / 31536000)} años`;
  };

  const isLicenseExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const isInstallationActive = (installation) => {
    // Si no hay heartbeat, considerar activa si la instalación está marcada como activa
    if (!installation.last_heartbeat) {
      return installation.is_active;
    }
    
    // Si hay heartbeat, verificar si es reciente (menos de 24 horas)
    const lastHeartbeatDate = new Date(installation.last_heartbeat);
    const now = new Date();
    const diffInHours = (now - lastHeartbeatDate) / (1000 * 60 * 60);
    return diffInHours < 24 && installation.is_active;
  };

  // Overview Tab Content
  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Client Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <UserIcon className="h-5 w-5 mr-2" />
            Información del Cliente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Nombre</label>
                <p className="text-gray-900">{client?.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Email</label>
                <p className="text-gray-900 flex items-center">
                  <EnvelopeIcon className="h-4 w-4 mr-1" />
                  {client?.email}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Empresa</label>
                <p className="text-gray-900 flex items-center">
                  <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                  {client?.company}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Tipo de Cliente</label>
                <Badge variant="info" className="mt-1">
                  {getTypeLabel(client?.client_type)}
                </Badge>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Estado</label>
                <div className="mt-1">
                  <Badge variant={getStatusBadgeVariant(client?.status)}>
                    {getStatusLabel(client?.status)}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Teléfono</label>
                <p className="text-gray-900 flex items-center">
                  <PhoneIcon className="h-4 w-4 mr-1" />
                  {client?.phone}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Dirección</label>
                <p className="text-gray-900 flex items-center">
                  <MapPinIcon className="h-4 w-4 mr-1" />
                  {client?.address}, {client?.city}, {client?.state}
                </p>
                {client?.postal_code && (
                  <p className="text-sm text-gray-500 ml-5">
                    CP: {client.postal_code}, {client?.country}
                  </p>
                )}
              </div>
              {client?.website && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Sitio Web</label>
                  <p className="text-gray-900 flex items-center">
                    <GlobeAltIcon className="h-4 w-4 mr-1" />
                    <a href={client.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {client.website}
                    </a>
                  </p>
                </div>
              )}
              {client?.phone_secondary && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Teléfono Secundario</label>
                  <p className="text-gray-900 flex items-center">
                    <PhoneIcon className="h-4 w-4 mr-1" />
                    {client.phone_secondary}
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <KeyIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Licencias Totales</p>
                <p className="text-2xl font-bold text-gray-900">{client?.license_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <ComputerDesktopIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Instalaciones</p>
                <p className="text-2xl font-bold text-gray-900">{client?.installation_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CalendarIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Cliente desde</p>
                <p className="text-sm font-bold text-gray-900">{formatDate(client?.created_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Info */}
      {(client?.contact_person || client?.notes || client?.tax_id) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <DocumentTextIcon className="h-5 w-5 mr-2" />
              Información Adicional
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {client?.contact_person && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Persona de Contacto</label>
                  <p className="text-gray-900">{client.contact_person}</p>
                  {client?.contact_position && (
                    <p className="text-sm text-gray-500">{client.contact_position}</p>
                  )}
                </div>
              )}
              {client?.tax_id && (
                <div>
                  <label className="text-sm font-medium text-gray-700">RFC/CUIT</label>
                  <p className="text-gray-900">{client.tax_id}</p>
                </div>
              )}
              {client?.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Notas</label>
                  <p className="text-gray-900">{client.notes}</p>
                </div>
              )}
              {client?.last_contact && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Último Contacto</label>
                  <p className="text-gray-900">{formatDate(client.last_contact)}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Licenses Tab Content
  const LicensesTab = () => (
    <div className="space-y-4">
      {licenses && licenses.length > 0 ? (
        <div className="space-y-4">
          {licenses.map((license) => (
            <Card key={license.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {license.software_name} v{license.software_version}
                      </h3>
                      <Badge variant={getLicenseStatusBadgeVariant(license.status)}>
                        {getLicenseStatusLabel(license.status)}
                      </Badge>
                      <Badge variant={getLicenseTypeBadgeVariant(license)}>
                        {getLicenseTypeLabel(license)}
                      </Badge>
                    </div>
                    {/* Software Information */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <ComputerDesktopIcon className="h-4 w-4 text-blue-600" />
                        <label className="font-medium text-gray-700">Software</label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">Nombre:</span>
                          <span className="ml-2 font-medium text-gray-900">{license.software_name}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Versión:</span>
                          <span className="ml-2 font-medium text-gray-900">{license.software_version}</span>
                        </div>
                        {license.software_description && (
                          <div className="md:col-span-2">
                            <span className="text-gray-600">Descripción:</span>
                            <p className="mt-1 text-gray-900 text-sm">{license.software_description}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Pricing Information */}
                    {license.price && (
                      <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center space-x-2 mb-2">
                          <CurrencyDollarIcon className="h-4 w-4 text-green-600" />
                          <label className="font-medium text-gray-700">Información de Precios</label>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-600">Precio Final:</span>
                            <span className="ml-2 font-bold text-green-700 text-lg">${license.price}</span>
                          </div>
                          {license.original_price && license.original_price !== license.price && (
                            <div>
                              <span className="text-gray-600">Precio Original:</span>
                              <span className="ml-2 text-gray-500 line-through">${license.original_price}</span>
                            </div>
                          )}
                          {license.discount_percentage && license.discount_percentage > 0 && (
                            <div>
                              <span className="text-gray-600">Descuento:</span>
                              <span className="ml-2 font-medium text-red-600">{license.discount_percentage}%</span>
                            </div>
                          )}
                          {license.promotion_name && (
                            <div className="md:col-span-2">
                              <span className="text-gray-600">Promoción:</span>
                              <span className="ml-2 font-medium text-blue-600">{license.promotion_name}</span>
                            </div>
                          )}
                          {license.promotion_description && (
                            <div className="md:col-span-2">
                              <span className="text-gray-600">Descripción de Promoción:</span>
                              <p className="mt-1 text-gray-700 text-sm">{license.promotion_description}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* License Information */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <label className="font-medium text-gray-700">Clave de Licencia</label>
                        <p className="text-gray-900 font-mono text-xs">{license.license_key}</p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">Instalaciones</label>
                        <p className="text-gray-900">
                          {license.current_installations} / {license.max_installations}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">Fecha de Emisión</label>
                        <p className="text-gray-900">{formatDate(license.issued_date)}</p>
                      </div>
                    </div>
                    {license.expiry_date && (
                      <div className="mt-2">
                        <label className="font-medium text-gray-700">Fecha de Expiración</label>
                        <div className="flex items-center space-x-2">
                          <p className="text-gray-900">{formatDate(license.expiry_date)}</p>
                          {isLicenseExpired(license.expiry_date) ? (
                            <Badge variant="danger" className="text-xs">
                              <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                              Expirada
                            </Badge>
                          ) : (
                            <Badge variant="success" className="text-xs">
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Válida
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <KeyIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay licencias</h3>
          <p className="text-gray-500">Este cliente no tiene licencias asignadas.</p>
        </div>
      )}
    </div>
  );

  // Installations Tab Content — lista compacta + acordeón (escala con muchos PCs e historial largo)
  const InstallationsTab = () => (
    <div className="flex flex-col min-h-0 max-h-[min(560px,calc(85vh-200px))]">
      <p className="text-xs text-gray-500 mb-3 shrink-0">
        Tocá una fila para ver detalle e historial. El historial muestra hasta 30 entradas con scroll interno.
      </p>
      <div className="space-y-2 overflow-y-auto pr-1 min-h-0 flex-1">
        {installations && installations.length > 0 ? (
          installations.map((installation) => {
            const expanded = expandedInstallationId === installation.id;
            const logCount = (installation.validation_logs || []).length;
            return (
              <Card key={installation.id} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex items-stretch gap-0">
                    <button
                      type="button"
                      className="flex-1 min-w-0 flex items-start gap-2 p-3 md:p-4 text-left hover:bg-gray-50/80 transition-colors"
                      onClick={() =>
                        setExpandedInstallationId(expanded ? null : installation.id)
                      }
                      aria-expanded={expanded}
                    >
                      <span className="mt-0.5 text-gray-400 shrink-0">
                        {expanded ? (
                          <ChevronDownIcon className="h-5 w-5" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5" />
                        )}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <CpuChipIcon className="h-4 w-4 text-gray-500 hidden sm:inline" />
                          <span className="font-semibold text-gray-900 truncate">
                            {installation.machine_name}
                          </span>
                          <Badge
                            variant={
                              isInstallationActive(installation) ? 'success' : 'warning'
                            }
                            className="text-xs shrink-0"
                          >
                            {isInstallationActive(installation) ? 'Activa' : 'Inactiva'}
                          </Badge>
                          {logCount > 0 ? (
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                              {logCount} en historial
                            </span>
                          ) : null}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          <span className="font-medium text-gray-600">
                            {installation.software_name}
                          </span>
                          {' · '}
                          <span className="font-mono">{installation.mac_address}</span>
                          {' · Últ. validación: '}
                          {installation.last_validation
                            ? formatDate(installation.last_validation, 'datetime')
                            : '—'}
                        </p>
                      </div>
                    </button>
                    <div className="flex items-start p-2 md:p-3 shrink-0 border-l border-gray-100 bg-white">
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        title="Liberar cupo en esta licencia (quitar este PC)"
                        disabled={releaseInstallationMutation.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReleaseInstallation(installation);
                        }}
                        className="whitespace-nowrap"
                      >
                        <TrashIcon className="h-4 w-4 mr-1 inline" />
                        <span className="hidden sm:inline">Liberar PC</span>
                        <span className="sm:hidden">Liberar</span>
                      </Button>
                    </div>
                  </div>

                  {expanded ? (
                    <div className="border-t border-gray-200 bg-slate-50/60 px-3 md:px-4 pb-4 pt-3 space-y-4">
                      <div className="p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex items-center space-x-2 mb-2">
                          <KeyIcon className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-gray-700 text-sm">
                            Software y licencia
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-600">Software (catálogo):</span>
                            <span className="ml-2 font-medium text-gray-900">
                              {installation.software_name} v{installation.software_version}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Tipo:</span>
                            <span className="ml-2 font-medium text-gray-900">
                              {getLicenseTypeLabel(installation)}
                            </span>
                          </div>
                          <div className="md:col-span-2">
                            <span className="text-gray-600">Clave:</span>
                            <p className="mt-0.5 font-mono text-xs text-gray-900 break-all">
                              {installation.license_key}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div className="sm:col-span-2">
                          <label className="font-medium text-gray-700 text-xs uppercase tracking-wide">
                            ID de instalación
                          </label>
                          <p className="text-gray-900 font-mono text-xs break-all mt-0.5">
                            {installation.client_installation_id ? (
                              installation.client_installation_id
                            ) : (
                              <>
                                <span className="text-gray-500">No asignado</span>
                                {installation.id != null && (
                                  <span className="block text-gray-400 mt-0.5 text-[11px]">
                                    ID interno: {installation.id}
                                  </span>
                                )}
                              </>
                            )}
                          </p>
                        </div>
                        <div>
                          <label className="font-medium text-gray-700 text-xs uppercase tracking-wide">
                            MAC
                          </label>
                          <p className="font-mono text-xs mt-0.5">{installation.mac_address}</p>
                        </div>
                        <div>
                          <label className="font-medium text-gray-700 text-xs uppercase tracking-wide">
                            IP
                          </label>
                          <p className="mt-0.5">{installation.ip_address || 'N/A'}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="font-medium text-gray-700 text-xs uppercase tracking-wide">
                            ID de máquina
                          </label>
                          <p className="font-mono text-xs break-all mt-0.5">
                            {installation.machine_id || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="font-medium text-gray-700 text-xs uppercase tracking-wide">
                            Instalación
                          </label>
                          <p className="mt-0.5">{formatDate(installation.installation_date)}</p>
                        </div>
                        <div>
                          <label className="font-medium text-gray-700 text-xs uppercase tracking-wide">
                            Heartbeat
                          </label>
                          <p className="mt-0.5">
                            {installation.last_heartbeat
                              ? `${formatDate(installation.last_heartbeat)} (${getTimeAgo(installation.last_heartbeat)})`
                              : 'Nunca'}
                          </p>
                        </div>
                        {installation.client_reported_version ? (
                          <div>
                            <label className="font-medium text-gray-700 text-xs uppercase tracking-wide">
                              Versión app
                            </label>
                            <p className="font-mono text-xs mt-0.5">
                              {installation.client_reported_version}
                            </p>
                          </div>
                        ) : null}
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <ClockIcon className="h-4 w-4 text-indigo-600 shrink-0" />
                          <span className="font-medium text-gray-800 text-sm">
                            Historial de validaciones
                          </span>
                          <span className="text-xs text-gray-500">(máx. 30 · scroll)</span>
                        </div>
                        {(installation.validation_logs || []).length > 0 ? (
                          <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
                            <div className="max-h-52 md:max-h-60 overflow-y-auto overscroll-contain">
                              <table className="min-w-full text-xs text-left">
                                <thead className="sticky top-0 z-[1] bg-gray-100 text-gray-600 shadow-[0_1px_0_0_rgb(229_231_235)]">
                                  <tr>
                                    <th className="px-2 py-2 font-medium whitespace-nowrap">
                                      Fecha
                                    </th>
                                    <th className="px-2 py-2 font-medium">Resultado</th>
                                    <th className="px-2 py-2 font-medium whitespace-nowrap">
                                      IP
                                    </th>
                                    <th className="px-2 py-2 font-medium min-w-[140px]">
                                      Detalle
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {installation.validation_logs.map((log) => (
                                    <tr key={log.id} className="bg-white">
                                      <td className="px-2 py-2 whitespace-nowrap text-gray-900 align-top">
                                        {log.validation_date
                                          ? formatDate(log.validation_date, 'datetime')
                                          : '—'}
                                      </td>
                                      <td className="px-2 py-2 align-top">
                                        <Badge
                                          variant={getValidationResultBadgeVariant(
                                            log.validation_result
                                          )}
                                          className="text-xs"
                                        >
                                          {getValidationResultLabel(log.validation_result)}
                                        </Badge>
                                      </td>
                                      <td className="px-2 py-2 font-mono text-gray-700 whitespace-nowrap align-top">
                                        {log.client_ip || '—'}
                                      </td>
                                      <td className="px-2 py-2 text-gray-600 align-top">
                                        <span className="line-clamp-2" title={log.details || ''}>
                                          {log.details || '—'}
                                        </span>
                                        {log.user_agent ? (
                                          <p
                                            className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[220px] md:max-w-xs"
                                            title={log.user_agent}
                                          >
                                            UA: {log.user_agent}
                                          </p>
                                        ) : null}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 py-2">
                            Sin entradas en el historial para esta instalación.
                          </p>
                        )}
                      </div>

                      {installation.notes ? (
                        <div className="text-sm border-t border-gray-200 pt-3">
                          <label className="font-medium text-gray-700">Notas</label>
                          <p className="text-gray-900 mt-0.5">{installation.notes}</p>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            );
          })
        ) : (
          <div className="text-center py-8">
            <ComputerDesktopIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay instalaciones</h3>
            <p className="text-gray-500">Este cliente no tiene instalaciones registradas.</p>
          </div>
        )}
      </div>
    </div>
  );

  const tabs = [
    {
      id: 'overview',
      label: 'Resumen',
      icon: <UserIcon className="h-4 w-4" />,
      content: <OverviewTab />
    },
    {
      id: 'licenses',
      label: 'Licencias',
      icon: <KeyIcon className="h-4 w-4" />,
      badge: client?.license_count || 0,
      content: <LicensesTab />
    },
    {
      id: 'installations',
      label: 'Instalaciones',
      icon: <ComputerDesktopIcon className="h-4 w-4" />,
      badge: client?.installation_count || 0,
      content: <InstallationsTab />
    }
  ];

  if (clientLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Detalles del Cliente" size="xl">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Cargando información del cliente...</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Detalles del Cliente" size="xl">
      <Tabs 
        tabs={tabs} 
        defaultTab={activeTab}
        onTabChange={setActiveTab}
        className="h-full"
      />
    </Modal>
  );
};

export { ClientDetailModal };
